import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  IconButton,
  Tooltip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  ArrowBack as BackIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  AccountBalanceWallet as WalletIcon,
  CardMembership as MembershipIcon,
  Refresh as RefreshIcon,
  Email as EmailIcon,
  Block as SuspendIcon,
  Close as RejectIcon,
} from '@mui/icons-material';
import {
  getAdminCreatorDashboard,
  getAdminCreatorPayouts,
  processCreatorPayout,
  extendCreatorMembership,
  approveCreator,
  rejectCreator,
  suspendCreator,
  resendCreatorWelcome,
  AdminCreatorDashboard,
  CreatorPayout,
} from '../adminApi';

const CreatorDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const creatorId = parseInt(id || '0');

  const [dashboard, setDashboard] = useState<AdminCreatorDashboard | null>(null);
  const [payouts, setPayouts] = useState<CreatorPayout[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Extend membership dialog
  const [extendDialogOpen, setExtendDialogOpen] = useState(false);
  const [extendMonths, setExtendMonths] = useState<number | ''>('');
  const [extendPreset, setExtendPreset] = useState<string>('');
  const [extendLoading, setExtendLoading] = useState(false);

  // Process payout dialog
  const [payoutDialogOpen, setPayoutDialogOpen] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState<CreatorPayout | null>(null);
  const [paymentReference, setPaymentReference] = useState('');
  const [payoutLoading, setPayoutLoading] = useState(false);

  // Action dialogs
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [actionReason, setActionReason] = useState('');
  const [terminationType, setTerminationType] = useState<'for_cause' | 'without_cause'>('without_cause');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!creatorId) return;
    setLoading(true);
    setError(null);
    try {
      const [dashboardData, payoutData] = await Promise.all([
        getAdminCreatorDashboard(creatorId),
        getAdminCreatorPayouts(creatorId, { limit: 20 }),
      ]);
      setDashboard(dashboardData);
      setPayouts(payoutData);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load creator details');
    } finally {
      setLoading(false);
    }
  }, [creatorId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCopyCode = () => {
    if (dashboard?.creator.promo_code) {
      navigator.clipboard.writeText(dashboard.creator.promo_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleExtendMembership = async () => {
    const months = extendMonths;
    if (!months || months < 1) return;

    setExtendLoading(true);
    setError(null);
    try {
      const result = await extendCreatorMembership(creatorId, months);
      setSuccessMessage(result.message);
      setExtendDialogOpen(false);
      setExtendMonths('');
      setExtendPreset('');
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to extend membership');
    } finally {
      setExtendLoading(false);
    }
  };

  const handleProcessPayout = async () => {
    if (!selectedPayout || !paymentReference.trim()) return;

    setPayoutLoading(true);
    setError(null);
    try {
      await processCreatorPayout(selectedPayout.payout_id, paymentReference.trim());
      setSuccessMessage('Payout processed successfully');
      setPayoutDialogOpen(false);
      setSelectedPayout(null);
      setPaymentReference('');
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to process payout');
    } finally {
      setPayoutLoading(false);
    }
  };

  const handleApprove = async () => {
    setActionLoading(true);
    setError(null);
    try {
      await approveCreator(creatorId);
      setSuccessMessage('Creator approved successfully');
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to approve creator');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!actionReason) return;
    setActionLoading(true);
    setError(null);
    try {
      await rejectCreator(creatorId, actionReason);
      setSuccessMessage('Creator rejected');
      setRejectDialogOpen(false);
      setActionReason('');
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reject creator');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSuspend = async () => {
    if (!actionReason) return;
    setActionLoading(true);
    setError(null);
    try {
      await suspendCreator(creatorId, actionReason, terminationType);
      setSuccessMessage('Creator suspended');
      setSuspendDialogOpen(false);
      setActionReason('');
      setTerminationType('without_cause');
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to suspend creator');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResendWelcome = async () => {
    setActionLoading(true);
    setError(null);
    try {
      const result = await resendCreatorWelcome(creatorId);
      setSuccessMessage(result.message);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to resend welcome email');
    } finally {
      setActionLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
    switch (status) {
      case 'active': return 'success';
      case 'approved': return 'info';
      case 'pending': return 'warning';
      case 'processing': return 'warning';
      case 'completed': return 'success';
      case 'suspended': return 'error';
      case 'rejected': return 'error';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const handlePresetChange = (preset: string) => {
    setExtendPreset(preset);
    switch (preset) {
      case '6mo': setExtendMonths(6); break;
      case '1yr': setExtendMonths(12); break;
      case '2yr': setExtendMonths(24); break;
      case 'custom': setExtendMonths(''); break;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !dashboard) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/admin/creators')} sx={{ mb: 2 }}>
          Back to Creators
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!dashboard) return null;

  const { creator, membership, referrals, revenue } = dashboard;
  const pendingPayouts = payouts.filter(p => p.status === 'pending');

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} mb={3}>
        <IconButton onClick={() => navigate('/admin/creators')}>
          <BackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="h4" fontWeight="bold">
              {creator.name}
            </Typography>
            <Chip label={creator.status} color={getStatusColor(creator.status)} size="small" />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {creator.email}
          </Typography>
        </Box>
        {creator.promo_code && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Chip
              label={creator.promo_code}
              variant="outlined"
              sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}
            />
            <Tooltip title={copied ? 'Copied!' : 'Copy code'}>
              <IconButton size="small" onClick={handleCopyCode}>
                {copied ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Stack>
        )}
        <IconButton onClick={fetchData} size="small">
          <RefreshIcon />
        </IconButton>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Membership Card */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <MembershipIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            Membership
          </Typography>
          {membership && (
            <Chip label={membership.status} size="small" color={getStatusColor(membership.status)} />
          )}
        </Stack>
        {membership ? (
          <Stack direction="row" spacing={4} alignItems="center">
            <Box>
              <Typography variant="body2" color="text.secondary">Start Date</Typography>
              <Typography fontWeight="medium">{formatDate(membership.start_date)}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">End Date</Typography>
              <Typography fontWeight="medium">{formatDate(membership.end_date)}</Typography>
            </Box>
            <Box sx={{ flex: 1 }} />
            <Button variant="outlined" onClick={() => setExtendDialogOpen(true)}>
              Extend Membership
            </Button>
          </Stack>
        ) : (
          <Typography color="text.secondary">No membership found (user account may not be linked)</Typography>
        )}
      </Paper>

      {/* Stats Grid */}
      <Grid container spacing={2} mb={3}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <PeopleIcon color="primary" fontSize="small" />
                <Typography variant="body2" color="text.secondary">Total Signups</Typography>
              </Stack>
              <Typography variant="h4" fontWeight="bold">{referrals.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <TrendingUpIcon color="success" fontSize="small" />
                <Typography variant="body2" color="text.secondary">Active Subscribers</Typography>
              </Stack>
              <Typography variant="h4" fontWeight="bold" color="success.main">{referrals.active}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <MoneyIcon color="primary" fontSize="small" />
                <Typography variant="body2" color="text.secondary">Total Earned</Typography>
              </Stack>
              <Typography variant="h4" fontWeight="bold">{formatCurrency(revenue.total_earned)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <WalletIcon color="warning" fontSize="small" />
                <Typography variant="body2" color="text.secondary">Pending Payout</Typography>
              </Stack>
              <Typography variant="h4" fontWeight="bold" color="warning.main">{formatCurrency(revenue.pending_payout)}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Referral Breakdown & Payout Section */}
      <Grid container spacing={3} mb={3}>
        {/* Referral Breakdown */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" fontWeight="bold" mb={2}>
              Referral Breakdown
            </Typography>
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">New this month</Typography>
                <Typography fontWeight="medium">{referrals.new_this_month}</Typography>
              </Stack>
              <Divider />
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">Currently trialing</Typography>
                <Chip label={referrals.trialing} size="small" color="info" />
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">Active subscribers</Typography>
                <Chip label={referrals.active} size="small" color="success" />
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">Cancelled</Typography>
                <Chip label={referrals.cancelled} size="small" color="default" />
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">Churned</Typography>
                <Chip label={referrals.churned} size="small" color="error" />
              </Stack>
            </Stack>
          </Paper>
        </Grid>

        {/* Payout Section */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" fontWeight="bold" mb={2}>
              Payouts
            </Typography>
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">Total paid out</Typography>
                <Typography fontWeight="medium">{formatCurrency(revenue.total_paid)}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">Pending payout</Typography>
                <Typography fontWeight="bold" color="warning.main">
                  {formatCurrency(revenue.pending_payout)}
                </Typography>
              </Stack>
              <Divider />
              <Typography variant="body2" color="text.secondary">
                Payouts are processed on the 1st of each month.
              </Typography>
              {pendingPayouts.length > 0 && (
                <Button
                  variant="contained"
                  onClick={() => {
                    setSelectedPayout(pendingPayouts[0]);
                    setPayoutDialogOpen(true);
                  }}
                >
                  Process Payout ({formatCurrency(pendingPayouts[0].amount)})
                </Button>
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* Payout History */}
      {payouts.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight="bold" mb={2}>
            Payout History
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payouts.map((payout) => (
                  <TableRow key={payout.payout_id}>
                    <TableCell>{payout.payout_id}</TableCell>
                    <TableCell>{formatDate(payout.created_at)}</TableCell>
                    <TableCell>
                      {formatDate(payout.period_start)} - {formatDate(payout.period_end)}
                    </TableCell>
                    <TableCell>{formatCurrency(payout.amount)}</TableCell>
                    <TableCell>
                      <Chip label={payout.status} size="small" color={getStatusColor(payout.status)} />
                    </TableCell>
                    <TableCell>{payout.payment_reference || '-'}</TableCell>
                    <TableCell>
                      {payout.status === 'pending' && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setSelectedPayout(payout);
                            setPayoutDialogOpen(true);
                          }}
                        >
                          Process
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Action Buttons */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight="bold" mb={2}>
          Actions
        </Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap">
          {creator.status === 'pending' && (
            <>
              <Button
                variant="contained"
                color="success"
                onClick={handleApprove}
                disabled={actionLoading}
                startIcon={<CheckIcon />}
              >
                Approve
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={() => setRejectDialogOpen(true)}
                disabled={actionLoading}
                startIcon={<RejectIcon />}
              >
                Reject
              </Button>
            </>
          )}
          {(creator.status === 'approved' || creator.status === 'active') && (
            <>
              <Button
                variant="outlined"
                onClick={handleResendWelcome}
                disabled={actionLoading}
                startIcon={<EmailIcon />}
              >
                Resend Welcome Email
              </Button>
              <Button
                variant="outlined"
                color="warning"
                onClick={() => setSuspendDialogOpen(true)}
                disabled={actionLoading}
                startIcon={<SuspendIcon />}
              >
                Suspend
              </Button>
            </>
          )}
        </Stack>
      </Paper>

      {/* Extend Membership Dialog */}
      <Dialog open={extendDialogOpen} onClose={() => setExtendDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Extend Creator Membership</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select how long to extend the membership. The extension will be added to the current end date
            {membership ? ` (${formatDate(membership.end_date)})` : ''}.
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Duration</InputLabel>
            <Select
              value={extendPreset}
              label="Duration"
              onChange={(e) => handlePresetChange(e.target.value)}
            >
              <MenuItem value="6mo">6 Months</MenuItem>
              <MenuItem value="1yr">1 Year</MenuItem>
              <MenuItem value="2yr">2 Years</MenuItem>
              <MenuItem value="custom">Custom</MenuItem>
            </Select>
          </FormControl>
          {extendPreset === 'custom' && (
            <TextField
              label="Months"
              type="number"
              fullWidth
              value={extendMonths}
              onChange={(e) => setExtendMonths(parseInt(e.target.value) || '')}
              inputProps={{ min: 1, max: 120 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setExtendDialogOpen(false); setExtendMonths(''); setExtendPreset(''); }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleExtendMembership}
            disabled={extendLoading || !extendMonths || extendMonths < 1}
          >
            {extendLoading ? <CircularProgress size={20} /> : `Extend by ${extendMonths || '...'} month(s)`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Process Payout Dialog */}
      <Dialog open={payoutDialogOpen} onClose={() => setPayoutDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Process Payout</DialogTitle>
        <DialogContent>
          {selectedPayout && (
            <Typography variant="body1" sx={{ mb: 2 }}>
              Processing payout of <strong>{formatCurrency(selectedPayout.amount)}</strong> for{' '}
              {creator.name}.
            </Typography>
          )}
          <TextField
            label="Payment Reference (e.g., PayPal transaction ID)"
            fullWidth
            required
            value={paymentReference}
            onChange={(e) => setPaymentReference(e.target.value)}
            placeholder="Enter payment reference..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setPayoutDialogOpen(false); setPaymentReference(''); }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleProcessPayout}
            disabled={payoutLoading || !paymentReference.trim()}
          >
            {payoutLoading ? <CircularProgress size={20} /> : 'Mark as Paid'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Creator Application</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to reject <strong>{creator.name}</strong>&apos;s application?
          </Typography>
          <TextField
            label="Rejection Reason"
            fullWidth
            required
            multiline
            rows={3}
            value={actionReason}
            onChange={(e) => setActionReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setRejectDialogOpen(false); setActionReason(''); }}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={actionLoading || !actionReason}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Suspend Dialog */}
      <Dialog open={suspendDialogOpen} onClose={() => setSuspendDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Suspend Creator</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to suspend <strong>{creator.name}</strong>? This will deactivate their promo code.
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Termination Type</InputLabel>
            <Select
              value={terminationType}
              label="Termination Type"
              onChange={(e) => setTerminationType(e.target.value as 'for_cause' | 'without_cause')}
            >
              <MenuItem value="without_cause">Without cause — commissions continue 180 days</MenuItem>
              <MenuItem value="for_cause">For cause (policy violation) — commissions stop immediately</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Suspension Reason"
            fullWidth
            required
            multiline
            rows={3}
            value={actionReason}
            onChange={(e) => setActionReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSuspendDialogOpen(false); setActionReason(''); setTerminationType('without_cause'); }}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleSuspend}
            disabled={actionLoading || !actionReason}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Suspend'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreatorDetail;
